// cosailor_isco/src/components/MessageCenter.js
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { SendHorizonal, RefreshCw, Clock } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { format } from "date-fns";
import { toast } from "./ui/use-toast";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const MessageCenter = ({ scheduleId, rsmId, rsmName }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollingRef = useRef(null);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  };

  // Function to fetch messages
  const fetchMessages = async () => {
    if (!scheduleId) return;

    setLoading(true);
    try {
      // Check if this is a BYOI itinerary (starts with ITN-) or regular schedule
      const isByoiItinerary = scheduleId && scheduleId.toString().startsWith('ITN-');
      const endpoint = isByoiItinerary 
        ? `/api/itineraries/byoi/${scheduleId}/messages`
        : `/schedules/${scheduleId}/messages`;
      
      const response = await axios.get(endpoint);
      if (response.data && Array.isArray(response.data)) {
        setMessages(response.data);
        // Scroll to bottom after messages load
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Send a new message
  const sendMessage = async () => {
    if (!newMessage.trim() || !scheduleId) return;

    setSending(true);
    try {
      // Check if this is a BYOI itinerary (starts with ITN-) or regular schedule
      const isByoiItinerary = scheduleId && scheduleId.toString().startsWith('ITN-');
      const endpoint = isByoiItinerary 
        ? `/api/itineraries/byoi/${scheduleId}/messages`
        : `/schedules/${scheduleId}/messages`;
      
      await axios.post(endpoint, {
        content: newMessage.trim(),
      });

      // Clear input and refresh messages
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  // Set up polling for new messages
  useEffect(() => {
    if (scheduleId) {
      fetchMessages();

      // Set up polling every 30 seconds
      pollingRef.current = setInterval(fetchMessages, 30000);

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [scheduleId]);

  // Handle keyboard submit
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="px-4 py-3 border-b">
        <CardTitle className="text-base">
          Messages {rsmName ? `with ${rsmName}` : ""}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-grow p-0 overflow-hidden">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea ref={scrollRef} className="h-[calc(100%-60px)] px-4 py-2">
            {messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`flex ${message.sender_id === user?.id ? "flex-row-reverse" : "flex-row"} items-start gap-2 max-w-[80%]`}
                    >
                      <Avatar className="h-8 w-8 mt-0.5">
                        <AvatarFallback>
                          {getInitials(message.sender_name)}
                        </AvatarFallback>
                      </Avatar>

                      <div
                        className={`flex flex-col ${message.sender_id === user?.id ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`rounded-lg p-3 ${
                            message.sender_id === user?.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>
                        </div>

                        <div className="flex items-center mt-1 gap-1 text-xs text-muted-foreground">
                          <span>{message.sender_name}</span>
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          <span>
                            {format(
                              new Date(message.created_at),
                              "MMM d, h:mm a",
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No messages yet. Start the conversation!
              </div>
            )}
          </ScrollArea>
        )}
      </CardContent>

      <CardFooter className="p-4 border-t mt-auto">
        <div className="flex w-full gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="flex-grow"
            rows={2}
          />
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="self-end"
          >
            {sending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <SendHorizonal className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default MessageCenter;
